import { useEffect, useMemo, useRef, useState } from "react";
import "./MatchWin.css";
import MatchCard from "./MatchCard";
import { MatchItem } from "../../app/models/matchItem";
import { CircularProgress, Typography } from "@mui/material";
import Swal from "sweetalert2";
import {v4 as uuid} from "uuid";
import { MatchWinProps } from "../../app/models/matchWinProps";
import { FormStateProps } from "../../app/models/formStateProps";
import { ModalConfig } from "../../app/models/modalConfig";
import { Player } from "../../app/models/player";
import agent from "../../app/api/agent";
import UserDataModal, { UserDataValues } from "../UserDataModal";
import { toast } from "react-toastify";

type Step = "game" | "scoreboard";

interface MatchWinGameProps {
  isActive?: boolean;
  collectUserData: number;
  matchWin: MatchWinProps['matchWin'];
  matchObjects?: MatchWinProps['matchObjects'];
  formState: FormStateProps['formState'];
  modalConfig: ModalConfig;
  campaignId: string;
  campaignTitle: string;
}

const shuffle = <T,>(arr: T[]) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const formatMMSS = (totalSeconds: number) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60)
    .toString()
    .padStart(2, "0");
  const ss = (s % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
};

const MatchWin: React.FC<MatchWinGameProps> = ({
  isActive,
  collectUserData,
  matchWin,
  matchObjects = [],
  formState,
  modalConfig,
  campaignId,
  campaignTitle
}) => {
  const [step, setStep] = useState<Step>("game");

  const [cardsArray, setCardsArray] = useState<MatchItem[]>([]);
  const [moves, setMoves] = useState(0);
  const [firstCard, setFirstCard] = useState<MatchItem | null>(null);
  const [secondCard, setSecondCard] = useState<MatchItem | null>(null);
  const [stopFlip, setStopFlip] = useState(false);
  const [wonPairs, setWonPairs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [animatingMatchedIds, setAnimatingMatchedIds] = useState<string[]>([]);
  const matchAudioRef = useRef<HTMLAudioElement | null>(null);
  const matchAnimationTimerRef = useRef<number | null>(null);

  // time/moves mode
  const isTimeMode = (matchWin?.matchWinType ?? 1) === 1;
  const gameDuration = Number(matchWin?.gameDuration) || 0; // seconds; 0 means no limit
  const movesLimit = !isTimeMode && matchWin?.moves != null ? Number(matchWin.moves) : null;

  // timer state (seconds elapsed)
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<number | null>(null);
  const [gameEnded, setGameEnded] = useState(false);

  // After-create scoreboard
  const [currentGameId, setCurrentGameId] = useState<string | null>(null);
  const [scoreboard, setScoreboard] = useState<{
    total: number;
    rank: number;
    entries: {
      rank: number;
      name: string;
      points: number;
      isActive: boolean;
    }[];
  } | null>(null);

  const [openModal, setOpenModal] = useState(false);
  const [player, setPlayer] = useState<Player | null>(null);
  const initialFormValues: any = {
    firstName: "",
    lastName: "",
    email: formState.formEmail ? "" : undefined,
    phone: formState.formPhone ? "" : undefined,
    company: formState.formCompany ? "" : undefined,
    gender: formState.formGender ? 1 : undefined,
    formAddPermissionChecked: false,
    formAddPermission2Checked: false,
    ...(formState.bonusField && { [formState.bonusField]: "" }),
  };
  const [formValues, setFormValues] = useState(initialFormValues);
  const [dataCollected, setDataCollected] = useState(false);

  const [hideMatchedCards, setHideMatchedCards] = useState(true);

  // grid based on tileAmount (3->3x4, 4->4x3, 5->4x4 with fallback)
  const { rows, cols, requiredPairs } = useMemo(() => {
    let r = 3, c = 4; // default 3x4 → 12 tiles → 6 pairs
    if (matchWin?.tileAmount === 4) {
      r = 4; c = 3; // 4x3
    } else if (matchWin?.tileAmount === 5) {
      r = 4; c = 4; // 4x4 → 16 tiles → 8 pairs
      // fallback: need 8 objects to build 8 pairs
      if ((matchObjects?.length || 0) < 8) {
        r = 3; c = 4; // fallback to 3x4
      }
    }
    return { rows: r, cols: c, requiredPairs: (r * c) / 2 };
  }, [matchWin?.tileAmount, matchObjects]);

  function NewGame() {
    if (matchAnimationTimerRef.current) {
      window.clearTimeout(matchAnimationTimerRef.current);
      matchAnimationTimerRef.current = null;
    }

    setStep("game");
    setLoading(true);

    // choose the number of unique objects we need
    const needed = requiredPairs;
    const pool = shuffle(matchObjects.slice()); // randomize source

    // If we have fewer than needed, just slice what we have (game will still work with fewer pairs)
    const chosen = pool.slice(0, Math.min(needed, pool.length));

    // Map matchObjects → MatchItem (two copies each)
    // objectValue → name, objectImage → img
    const baseCards: MatchItem[] = chosen.flatMap((obj, idx) => {
      const name = obj.objectValue ?? `obj-${idx}`;
      const img = obj.objectImage ?? "";
      const a: MatchItem = { id: `${idx}-a`, name, img, matched: false };
      const b: MatchItem = { id: `${idx}-b`, name, img, matched: false };
      return [a, b];
    });

    const deck = shuffle(baseCards);

    setCardsArray(deck);
    setMoves(0);
    setFirstCard(null);
    setSecondCard(null);
    setStopFlip(false);
    setWonPairs(0);
    setAnimatingMatchedIds([]);
    setElapsed(0);
    setGameEnded(false);
    setCurrentGameId(null);
    setScoreboard(null);

    if (collectUserData === 2) {
      setDataCollected(false);
      setOpenModal(false);
      setPlayer(null);
    }

    setLoading(false);
  }

  function handleSelectedCards(item: MatchItem) {
    if (collectUserData === 2 && !dataCollected) return;
    if (stopFlip || gameEnded || step !== "game") return;
    if (firstCard !== null && firstCard.id !== item.id) {
      if (firstCard.name === item.name) {
        const matchAudio = matchAudioRef.current;
        if (matchAudio) {
          matchAudio.currentTime = 0;
          void matchAudio.play().catch(() => {
            // Keep the game usable if a browser blocks media playback.
          });
        }
      }
      setSecondCard(item);
    } else {
      setFirstCard(item);
    }
  }

  // if two have been selected then we check if the images are same or not,
  // if they are same then we stop the flipping ability
  // else we turn them back
  useEffect(() => {
    if (firstCard && secondCard) {
      setStopFlip(true);
      if (firstCard.name === secondCard.name) {
        const matchedIds = [firstCard.id, secondCard.id];

        setCardsArray((prevArray) =>
          prevArray.map((unit) =>
            unit.name === firstCard.name ? { ...unit, matched: true } : unit
          )
        );
        setAnimatingMatchedIds(matchedIds);

        matchAnimationTimerRef.current = window.setTimeout(() => {
          setAnimatingMatchedIds([]);
          setWonPairs((p) => p + 1);
          removeSelection();
          matchAnimationTimerRef.current = null;
        }, 700);
      } else {
        setTimeout(() => {
          removeSelection();
        }, 800);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstCard, secondCard]);

  useEffect(() => {
    return () => {
      if (matchAnimationTimerRef.current) {
        window.clearTimeout(matchAnimationTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isActive === false) {
      Swal.fire({
        title: "Oyun Bitti",
        text: "Oyun sona erdi. Katıldığınız için teşekkürler.",
        icon: "warning",
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showCloseButton: false,
        showConfirmButton: false,
      });
    }
  }, [isActive]);

  // after the selected images have been checked for
  // equivalency we empty the firstCard and secondCard component
  function removeSelection() {
    setFirstCard(null);
    setSecondCard(null);
    setStopFlip(false);
    setMoves((prev) => prev + 1);
  }

  // start immediately (no timeout), show loading until deck is prepared
  useEffect(() => {
    NewGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, cols]);

  // Timer: run only in time mode and only while game not ended and campaign active
  useEffect(() => {
    if (step !== "game") return;
    if (!isTimeMode || loading || gameEnded || isActive === false) return;
    if (collectUserData === 2 && !dataCollected) return;

    // clear if any
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    timerRef.current = window.setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [step, isTimeMode, loading, gameEnded, isActive, collectUserData, dataCollected]);

  // ---- Scoreboard helpers (windowed, reversed) ----
  const goScoreboard = async (gameId?: string) => {
    const id = gameId ?? currentGameId;
    if (id) {
      try {
        const sb = await agent.Games.scoreboard(campaignId, id, 10, true /* reversed */);
        setScoreboard(sb);
      } catch (e) {
        console.error("Failed to load scoreboard", e);
      }
    }
    setStep("scoreboard");
  };

  const goReplay = () => {
    if (collectUserData === 2) {
      setDataCollected(false);
      setOpenModal(false);
      setPlayer(null);
    }
    // reset and go back to game
    NewGame();
  };

  const finalizeMatchWin = async (won: boolean) => {
    const timeCompleted = isTimeMode ? elapsed : moves;
    const newGameId = uuid();

    // Build/select player by collection mode
    let effectivePlayer: Player | null = null;

    if (collectUserData === 2 && player) {
      // BEFORE: we already have player in state
      effectivePlayer = player;
    } else if (collectUserData === 3) {
      // AFTER: create placeholder now; update later via modal submit
      const pid = player?.id || uuid();
      effectivePlayer = {
        id: pid,
        campaignId,
        firstName: "",
        lastName: "",
        gender: 0,
        email: "",
        phone: "",
        company: "",
        formAddPermissionChecked: false,
        formAddPermission2Checked: false,
        bonusField: "",
        bonusFieldLabel: "",
      };
    } else {
      // NONE: anonymous
      const pid = uuid();
      effectivePlayer = {
        id: pid,
        campaignId,
        firstName: "",
        lastName: "",
        gender: 0,
        email: "",
        phone: "",
        company: "",
        formAddPermissionChecked: false,
        formAddPermission2Checked: false,
        bonusField: "",
        bonusFieldLabel: "",
      };
    }

    const basePayload = {
      id: newGameId,
      gameType: 5, // MatchWin
      prize: isTimeMode ? `${elapsed} saniye` : `${moves} hamle`,
      isPrize: won, // reflect win/lose
      hasWon: won, // must mirror isPrize in your reporting
      datePlayed: new Date().toISOString(),
      campaignId,
      timeCompleted, // seconds or moves
      points: isTimeMode ? elapsed : moves,
      playerId: effectivePlayer.id,
      player: effectivePlayer,
    };

    try {
      await agent.Games.create(basePayload as any);
      setPlayer(effectivePlayer);
      setCurrentGameId(newGameId);
      if (collectUserData === 3) {
        setOpenModal(true);
        return;
      }
    } catch (e) {
      console.error("Failed to create MatchWin game:", e);
    }

    await goScoreboard(newGameId);
  };

  // Win detection
  useEffect(() => {
    if (collectUserData === 2 && !dataCollected) return;
    if (gameEnded) return;
    if (wonPairs === requiredPairs && requiredPairs > 0) {
      setGameEnded(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      const msg =
        isTimeMode
          ? `Oyunu Tamamladığınız Süre: ${formatMMSS(elapsed)}`
          : `Oyunu Tamamladığınız Hamle: ${moves}`;

      Swal.fire({
        title: "Tebrikler!",
        text: msg,
        icon: "success",
        showConfirmButton: false,
        allowEscapeKey: false,
        didOpen: () => {
          document.addEventListener('click', () => {
            Swal.close();
          });
        },
      }).then(async (result) => {
        await finalizeMatchWin(true);
      });
    }
  }, [wonPairs, requiredPairs, isTimeMode, elapsed, moves, gameEnded, collectUserData, dataCollected]);

  // Time-based lose condition if countdown enabled
  useEffect(() => {
    if (collectUserData === 2 && !dataCollected) return;
    if (!isTimeMode || gameEnded) return;
    if (gameDuration > 0) {
      const remaining = gameDuration - elapsed;
      if (remaining <= 0 && wonPairs < requiredPairs) {
        setGameEnded(true);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        Swal.fire({
          title: "Süre Bitti",
          text: "Süre doldu! Katıldığınız için teşekkürler.",
          icon: "info",
          showConfirmButton: false,
          allowEscapeKey: false,
          didOpen: () => {
            document.addEventListener('click', () => {
              Swal.close();
            });
          },
        }).then(async (result) => {
          await finalizeMatchWin(false);
        });
      }
    }
  }, [elapsed, isTimeMode, gameDuration, wonPairs, requiredPairs, gameEnded, collectUserData, dataCollected]);

  // Moves-based lose condition if limit provided
  useEffect(() => {
    if (collectUserData === 2 && !dataCollected) return;
    if (isTimeMode || gameEnded) return;
    if (movesLimit != null && movesLimit >= 0) {
      if (moves >= movesLimit && wonPairs < requiredPairs) {
        setGameEnded(true);
        Swal.fire({
          title: "Hamleler Bitti",
          text: "Hamle hakkınız tükendi! Katıldığınız için teşekkürler.",
          icon: "info",
          showConfirmButton: false,
          allowEscapeKey: false,
          didOpen: () => {
            document.addEventListener('click', () => {
              Swal.close();
            });
          },
        }).then(async (result) => {
          await finalizeMatchWin(false);
        });
      }
    }
  }, [moves, isTimeMode, movesLimit, wonPairs, requiredPairs, gameEnded, collectUserData, dataCollected]);

  const buildPlayerData = (formValues: any, formState: FormStateProps['formState'], campaignId: string, playerId: string) => {
    return {
      id: playerId,
      campaignId,
      firstName: formValues.firstName || "",
      lastName: formValues.lastName || "",
      gender: formState.formGender ? formValues.gender === 1 ? 1 : 2 : 0,
      email: formState.formEmail ? formValues.email || "" : "",
      phone: formState.formPhone ? formValues.phone || "" : "",
      company: formState.formCompany ? formValues.company || "" : "",
      formAddPermissionChecked: formState.formAddPermission ? formValues.formAddPermissionChecked || false : false,
      formAddPermission2Checked: formState.formAddPermission2 ? formValues.formAddPermission2Checked || false : false,
      bonusField: formState.bonusField ? formValues[formState.bonusField] || "" : "",
      bonusFieldLabel: collectUserData != 1 ? formState.bonusField || "" : "",
    };
  };  

  const hasDuplicatePlayer = async (campaignId: string, values: UserDataValues) => {
    if (!formState.duplicateCheck) return false;
    const res = await agent.Games.exists(campaignId, {
      firstName: values.firstName,
      lastName: values.lastName,
      email: formState.formEmail ? values.email : undefined,
      phone: formState.formPhone ? values.phone : undefined,
    });
    return res.duplicate;
  };

  const handleSubmit = async (values: UserDataValues) => {
    if (await hasDuplicatePlayer(campaignId, values)) {
      toast.error("Aynı oyuncu bir kampanyada sadece bir kez oynayabilir.");
      return false; // keep modal open, do NOT reset anything
    }

    const playerId = player?.id || uuid();
    const playerData = buildPlayerData(values, formState, campaignId, playerId);

    if (collectUserData === 2) {
      setPlayer(playerData);
      setDataCollected(true);
      return true;
    }
    
    if (collectUserData === 3) {
      await agent.Players.update(playerData);
      setOpenModal(false);
      
      if (currentGameId) {
        await goScoreboard(currentGameId);
      }

      return true;
    }

    await agent.Players.update(playerData);
    return true;
  };

  const handleClose = () => {
    if (collectUserData != 2 && !dataCollected) {
      setPlayer(null);
    }    
    setOpenModal(false);
  };

  // grid style for board
  const boardStyle: React.CSSProperties = {
    "--matchwin-cols": cols,
    "--matchwin-rows": rows,
    pointerEvents: collectUserData === 2 && !dataCollected ? "none" : "auto",
    opacity: collectUserData === 2 && !dataCollected ? 0.5 : 1
  } as React.CSSProperties;

  // Top indicator
  const topIndicator = (() => {
    if (step !== "game") return null;

    if (isTimeMode) {
      const label = gameDuration > 0 ? "Süre" : "Süre";
      const display = gameDuration > 0 ? Math.max(gameDuration - elapsed, 0) : elapsed;
      return (
        <Typography className="matchwin-indicator" variant="h6" align="center">
          {label}: {formatMMSS(display)}
        </Typography>
      );
    } else {
      if (movesLimit != null && movesLimit >= 0) {
        const remaining = Math.max(movesLimit - moves, 0);
        return (
          <Typography className="matchwin-indicator" variant="h6" align="center">
            Kalan Hamle: {remaining}
          </Typography>
        );
      }
      return (
        <Typography className="matchwin-indicator" variant="h6" align="center">
          Hamle: {moves}
        </Typography>
      );
    }
  })();

  return (
    <div className="customgame-container matchwin">
      <audio
        ref={matchAudioRef}
        src={`${process.env.PUBLIC_URL}/assets/match-effect.mp3`}
        preload="auto"
      />
      {step === "game" && (
        <>
          {topIndicator}

          {loading ? (
            <div style={{ display: "grid", placeItems: "center", height: "60vh" }}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <div
                className={`board matchwin-board matchwin-board--${cardsArray.length}`}
                style={boardStyle}
              >
                {cardsArray.map((item: MatchItem) => {
                  const isMatchAnimating = animatingMatchedIds.includes(item.id);
                  const shouldHide =
                    hideMatchedCards && item.matched && !isMatchAnimating;
                  return (
                    <div
                      key={item.id}
                      className={isMatchAnimating ? "matchwin-card-matched" : undefined}
                      style={{ visibility: shouldHide ? "hidden" : "visible" }}
                    >
                      <MatchCard
                        item={item}
                        handleSelectedCards={handleSelectedCards}
                        toggled={item === firstCard || item === secondCard || item.matched === true}
                        stopflip={stopFlip}
                        color={matchWin.coverColor}
                        coverImage={matchWin.coverImage}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {step === "scoreboard" && (
        <div className="step-box scoreboard">
          {/* Reuse TouchWin scoreboard visuals */}
          <h4 className="score-title">Sıralama</h4>
          <div className="scoreboard-container no-scrollbar">
            {scoreboard?.entries?.length
              ? scoreboard.entries.map((row) => (
                  <div
                    key={row.rank}
                    className={`score-row ${row.isActive ? "active" : ""}`}
                  >
                    <span className="rank">{row.rank}.</span>
                    <span className="name">{row.name || "—"}</span>
                    <span className="pts">
                      {row.points}
                      {isTimeMode ? " sn" : " hamle"}
                    </span>
                  </div>
                ))
              : // fallback skeleton if not loaded yet
                [...Array(10)].map((_, i) => (
                  <div key={i} className="score-row">
                    <span className="rank">{i + 1}.</span>
                    <span className="name">———</span>
                    <span className="pts">0</span>
                  </div>
                ))}
          </div>

          <button onClick={goReplay} className="form-button">
            <img
              src={`${process.env.PUBLIC_URL}/assets/end-button-2.png`}
              className="startpage-button"
              alt="Tekrar Oyna"
            />
          </button>
        </div>
      )}

      <UserDataModal
        open={collectUserData === 2 && !dataCollected ? true : openModal}
        onClose={handleClose}
        formState={formState}
        initialValues={initialFormValues}
        onSubmit={handleSubmit}
        submitLabel="Gönder"
      />
    </div>
  );
};

export default MatchWin;
